import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { RequestParamsType } from '@monsido/ng2/external/http/options';
import type { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import type {
    SiteAnalyticsBreadcrumbItem,
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsScaffoldConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';
import {
    buildLocationParentColumnAggregates,
    getCurrentPage,
    getNameLabel,
    getPerPage,
    getTotal,
    normalizeVisitorsRows,
} from '@client/app/feature-modules/site-analytics/definitions/visitors-definition.utils';

const VISITORS_LOCATION_CITIES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.visitors.locations.header',
    pageSubheaderKey: 'siteAnalytics.visitors.locations.cities.subheader',
    tableKey: 'site-analytics-visitors-location-cities',
    dataSource: 'visitorsLocationCities',
    exportCategory: undefined,
    searchSupported: false,
    emptyMessageKey: 'siteAnalytics.visitors.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.visitors.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.visitors.locations.cities.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.visitors.locations.cities.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    breadcrumbsBuilder: (routeParams: Record<string, unknown>): SiteAnalyticsBreadcrumbItem[] => {
        const countryCode = toParamString(routeParams.countryCode);
        const countryName = toParamString(routeParams.countryName) || countryCode;
        const regionCode = toParamString(routeParams.regionCode);
        const regionName = toParamString(routeParams.regionName) || regionCode;

        return [
            {
                labelKey: 'siteAnalytics.visitors.locations.breadcrumb.all',
                state: 'base.customer.domain.statistics.visitors.locations.index',
            },
            {
                labelKey: 'siteAnalytics.visitors.locations.breadcrumb.country',
                state: 'base.customer.domain.statistics.visitors.locations.index',
            },
            {
                label: countryName,
                state: 'base.customer.domain.statistics.visitors.locations.regions',
                params: {
                    countryCode,
                    countryName,
                },
            },
            {
                label: regionName,
            },
        ];
    },
    columns: [
        {
            id: 'name',
            headerKey: 'siteAnalytics.scaffold.table.column.name',
            displayType: 'text',
            width: 'xl',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'actionsPerVisit',
            headerKey: 'siteAnalytics.scaffold.table.column.actionsPerVisit',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            width: 'md',
        },
    ],
};

const loadVisitorsLocationCitiesData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const countryCode = toParamString(request.routeParams?.countryCode);
    const regionCode = toParamString(request.routeParams?.regionCode);
    const regionName = toParamString(request.routeParams?.regionName) || regionCode;

    const params: RequestParamsType = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
    });

    if (countryCode) {
        params.country_code = countryCode;
    }

    if (regionName) {
        params.region_label = regionName;
    }

    const [citiesResponse, parentRegionsResponse] = await Promise.all([
        statsRepoService.getCities(params, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getRegions({
            domainId: request.domainId,
            from: params.from,
            to: params.to,
            search: regionName,
        }, contextTokens.NO_PARSE_SEARCH),
    ]);
    const rows = normalizeVisitorsRows(citiesResponse);

    const parentSummary = Array.isArray(parentRegionsResponse) && parentRegionsResponse.length > 0
        ? parentRegionsResponse[0] as unknown as StatisticsSummary
        : null;

    return {
        rows,
        chart: {
            labels: rows.map((row) => getNameLabel(row)),
            primaryValues: rows.map((row) => toNumber(row.visits)),
        },
        summary: parentSummary,
        columnAggregates: buildLocationParentColumnAggregates(parentSummary),
        total: getTotal(citiesResponse, rows.length),
        perPage: getPerPage(citiesResponse, request.perPage),
        page: getCurrentPage(citiesResponse, request.page),
    };
};

function toParamString (value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }

    return value;
}

function toNumber (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const VISITORS_LOCATION_CITIES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'visitorsLocationCities',
    config: VISITORS_LOCATION_CITIES_CONFIG,
    loadData: loadVisitorsLocationCitiesData,
};

export const VISITORS_LOCATION_CITIES_PAGE_CONFIG = VISITORS_LOCATION_CITIES_DEFINITION.config;
